/** One live session's report, read: who came, what the room reacted to, the questions still waiting, the replay. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { LiveReactionsResponse } from "../../../shared/live-reactions";
import type { LiveQuestion, RoomPerson, WatchChapter, WebinarLive } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { directPath } from "../../talk/places";
import { busiestMarks, replayPill } from "./liveWords";

export const mins = (sec: number) => Math.max(0, Math.round(sec / 60));

/** `live` is null while the session is still being read. */
export function useSessionReport(webinarId: string, live: WebinarLive | null, seriesSession: boolean) {
  const navigate = useNavigate();
  const toast = useToast();
  const [came, setCame] = useState<{ people: RoomPerson[] } | null>(null);
  const [reactions, setReactions] = useState<LiveReactionsResponse | null>(null);
  const [answered, setAnswered] = useState<string[]>([]);
  const [replying, setReplying] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const sessionId = live?.sessionId ?? "";
  const report = live?.report;

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    api.webinars
      .audienceGroup(webinarId, "came", sessionId)
      .then((r) => alive && setCame({ people: r.people }))
      .catch(() => alive && setCame({ people: [] }));
    api.studio
      .reactions(webinarId, "all", seriesSession ? sessionId : undefined)
      .then((r) => alive && setReactions(r))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [webinarId, sessionId, seriesSession]);

  const marks = useMemo(() => busiestMarks(reactions?.timeline.moments ?? [], report), [reactions, report]);
  const chapters = useMemo<WatchChapter[]>(
    () => (report?.chapters ?? []).map((c) => ({ t: c.fromSec, title: c.name, sub: "" })),
    [report],
  );
  const waiting = (live?.questions ?? []).filter((q) => q.answered === null && !answered.includes(q.id));
  const openThread = (q: LiveQuestion) =>
    api.talk
      .directFor(q.registrantId)
      .then(({ contactId }) => navigate(directPath(contactId)))
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "We couldn't open their conversation"));

  return {
    came,
    reactions,
    answered,
    markAnswered: (ids: string[]) => setAnswered((was) => [...was, ...ids]),
    replying,
    setReplying,
    playing,
    setPlaying,
    sessionId,
    report,
    marks,
    chapters,
    waiting,
    lengthSec: report?.lengthSec ?? (live?.lengthMin ?? 0) * 60,
    pill: live ? replayPill(live) : { label: "", tone: "plain" as const },
    playable: live?.replay.playable ?? false,
    stayedPct: live?.cameLive ? Math.round((live.stayedToEnd / live.cameLive) * 100) : 0,
    openThread,
  };
}
